import { useEffect, useState } from "react";
import { ExternalLink, Plus, Star, MousePointerClick, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PagesJaunes() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ review_count: "", average_rating: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pagesjaunes").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addLog = async (e) => {
    e.preventDefault();
    if (form.review_count === "" || form.average_rating === "") {
      toast.error("Renseignez le nombre d'avis et la note.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/pagesjaunes/log", {
        review_count: parseInt(form.review_count, 10),
        average_rating: parseFloat(form.average_rating),
        note: form.note,
      });
      setForm({ review_count: "", average_rating: "", note: "" });
      toast.success("Relevé ajouté");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full bg-[#0a0b0e] border border-[#262a33] rounded-xl px-3 py-2 text-white text-sm outline-none focus:ring-2 focus:ring-[#00e676]";

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00e676] animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Suivi des avis</h1>
        <p className="text-[#64697a] mt-1">Fiches PagesJaunes & Google et évolution de la réputation</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5">
          <div className="flex items-center gap-2 text-[#a0a5b5] text-sm mb-3"><MousePointerClick className="w-4 h-4" /> Clics vers PagesJaunes</div>
          <div className="font-display text-4xl font-extrabold text-[#00e676] mb-3" data-testid="pj-total-clicks">{data.total_clicks}</div>
          <div className="text-xs text-[#64697a] mb-2 break-all">{data.pagesjaunes_url}</div>
          <a href={data.pagesjaunes_url} target="_blank" rel="noopener noreferrer" data-testid="open-pj-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#00e676] hover:bg-[#00c853] text-[#04120a] font-bold rounded-full py-2.5 px-4 transition-colors">
            <ExternalLink className="w-4 h-4" /> Ouvrir PagesJaunes
          </a>
        </div>

        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5">
          <div className="flex items-center gap-2 text-[#a0a5b5] text-sm mb-3"><MousePointerClick className="w-4 h-4" /> Clics vers Google</div>
          <div className="font-display text-4xl font-extrabold text-[#4285F4] mb-3" data-testid="google-total-clicks">{data.total_google_clicks ?? 0}</div>
          {data.google_url ? (
            <>
              <div className="text-xs text-[#64697a] mb-2 break-all">{data.google_url}</div>
              <a href={data.google_url} target="_blank" rel="noopener noreferrer" data-testid="open-google-btn"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#f1f3f4] text-[#1a1a1a] font-bold rounded-full py-2.5 px-4 transition-colors">
                <ExternalLink className="w-4 h-4" /> Ouvrir Google
              </a>
            </>
          ) : (
            <p className="text-xs text-[#64697a]">Ajoutez votre lien Google dans <span className="text-[#00e676]">Paramètres</span> pour activer le bouton Google sur la page client.</p>
          )}
        </div>
      </div>

      <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5">
        <h3 className="font-semibold mb-1">Relevé manuel des avis</h3>
        <p className="text-xs text-[#64697a] mb-4">Notez régulièrement l'évolution du nombre d'avis et de la note moyenne (les avis PagesJaunes ne sont pas récupérés automatiquement).</p>
        <form onSubmit={addLog} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-[#64697a] block mb-1">Nombre d'avis</label>
            <input type="number" value={form.review_count} onChange={(e) => setForm({ ...form, review_count: e.target.value })} className={inputCls} data-testid="pj-review-count" />
          </div>
          <div>
            <label className="text-xs text-[#64697a] block mb-1">Note moyenne</label>
            <input type="number" step="0.1" value={form.average_rating} onChange={(e) => setForm({ ...form, average_rating: e.target.value })} className={inputCls} data-testid="pj-rating" />
          </div>
          <div>
            <label className="text-xs text-[#64697a] block mb-1">Note (facultatif)</label>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} data-testid="pj-note" />
          </div>
          <button disabled={saving} data-testid="pj-add-log" className="flex items-center justify-center gap-2 bg-[#2a2d35] hover:bg-[#363a45] text-white font-medium rounded-xl px-4 py-2 transition-colors">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </form>
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold">Historique</h3>
        {data.logs.length === 0 ? (
          <div className="text-[#64697a] text-sm py-6 text-center">Aucun relevé enregistré.</div>
        ) : (
          data.logs.map((l) => (
            <div key={l.id} className="bg-[#161920] border border-[#262a33] rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 font-semibold"><Star className="w-4 h-4 fill-[#00e676] text-[#00e676]" /> {l.average_rating}</div>
                <div className="text-sm text-[#a0a5b5]">{l.review_count} avis</div>
                {l.note && <div className="text-xs text-[#64697a]">· {l.note}</div>}
              </div>
              <div className="text-xs text-[#64697a]">{(l.created_at || "").slice(0, 10)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
